import { useContext, useEffect } from 'react'

import Button from '~/core/Button'
import Link from '~/core/Link'
import Box from '~/core/Box'
import classList from '~/lib/classList'
import logger from '~/lib/logger'
import FictivLogo from '~/components/FictivLogo'
import { getenv } from '~/settings'
import { NavigationContext } from '~/core/NavigationConfig'

import slinky from './slinky.svg'
import styles from './styles.css'

const NOT_FOUND_CODE = 404
const INTERNAL_ERROR_CODE = 500
const PLACEMENT = 'broken-page'
const STATUS_MAP = {
    [`${NOT_FOUND_CODE}`]: {
        code: NOT_FOUND_CODE,
        subhead: "Oops, you've slinked too far",
        subtext: "We can't quite find what you're looking for.",
        helpMessage: "We're here to help!",
    },
    [`${INTERNAL_ERROR_CODE}`]: {
        code: INTERNAL_ERROR_CODE,
        subhead: 'Oops',
        subtext: 'Something went wrong',
        message: "We've automatically notified our team of this error.",
        helpMessage: 'Need help right away?',
    },
}

interface BrokenPagePropsType {
    status: number
}

const BrokenPage = (props: BrokenPagePropsType) => {
    const { status = NOT_FOUND_CODE } = props

    useEffect(() => {
        classList(document.body).add(styles.body)
        return () => {
            classList(document.body).remove(styles.body)
        }
    }, [])

    const { setNavigationConfig } = useContext(NavigationContext)

    useEffect(() => {
        setNavigationConfig({})
    }, [])

    const logReport = (e: Event) => {
        e.preventDefault()
        logger.openErrorFeedback()
    }

    const { code, subhead, subtext, message, helpMessage } = STATUS_MAP[status]

    return (
        <Box
            alignItems="center"
            direction="col"
            style={{
                height: '100%',
                width: '100%',
            }}
        >
            <Box
                element="header"
                alignItems="center"
                direction="col"
                className={styles.header}
            >
                <Box alignSelf="start" display="block" className={styles.logo}>
                    <Link
                        placement={PLACEMENT}
                        name="fictiv-logo"
                        href={`${getenv('REGIONAL_CONFIGS')?.urls?.fictiv}`}
                    >
                        <FictivLogo fill="gray" />
                    </Link>
                </Box>
                <img style={{ width: '84%' }} src={slinky} />
            </Box>
            <Box
                direction="col"
                alignItems="center"
                justifyContent="center"
                element="article"
                className={styles.article}
            >
                <h1
                    style={{
                        fontSize: 112,
                        margin: 0,
                        marginTop: 20,
                    }}
                >
                    {code}
                </h1>
                <h2>{subhead}</h2>
                <p>{subtext}</p>
                {message && <p>{message}</p>}
                <p>{helpMessage} Give us a call at (415) 580-2509.</p>
                <Box alignItems="center" justifyContent="between">
                    <a
                        data-test-target="go-back"
                        href="/pages/projects"
                        style={{ marginRight: 16 }}
                    >
                        Go back home
                    </a>
                    {status === INTERNAL_ERROR_CODE && (
                        <Button
                            placement={PLACEMENT}
                            name="log-report"
                            onClick={logReport}
                        >
                            Tell us what happened
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default BrokenPage
